import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { auth } from '@/firebase';
import { configurePdfJsWorker } from '@/lib/pdfjsWorker';

const resolvePdfProxyBaseUrl = (explicitProxyBaseUrl?: string): string | null => {
  if (explicitProxyBaseUrl) return explicitProxyBaseUrl;

  const envProxy = (import.meta as any).env?.VITE_PDF_PROXY_URL as string | undefined;
  if (envProxy) return envProxy;

  const projectId = (import.meta as any).env?.VITE_FIREBASE_PROJECT_ID as string | undefined;
  if (projectId) return `https://asia-northeast1-${projectId}.cloudfunctions.net/pdfProxy`;

  return null;
};

interface Props {
  shikakuId: string;
  shikakuName?: string;
  proxyBaseUrl?: string;
  onPageInfoChange?: (page: number, total: number) => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 2.6;
const BASE_RENDER_SCALE = 1.6;

type PanState = { x: number; y: number };

const PageCanvas = React.memo(function PageCanvas({
  pdf,
  pageNumber,
  watermark,
}: {
  pdf: any;
  pageNumber: number;
  watermark: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    let renderTask: any = null;

    const renderPage = async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: BASE_RENDER_SCALE });
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        renderTask = page.render({ canvasContext: ctx, viewport });
        await renderTask.promise;

        ctx.save();
        ctx.globalAlpha = 0.12;
        ctx.font = 'bold 24px sans-serif';
        ctx.fillStyle = '#000';
        ctx.fillText(watermark || 'User', 20, canvas.height - 30);
        ctx.restore();
      } catch (err) {
        if (!cancelled) console.error('Page render error', err);
      }
    };

    renderPage();
    return () => {
      cancelled = true;
      try {
        renderTask?.cancel?.();
      } catch {
        // noop
      }
    };
  }, [pdf, pageNumber, watermark]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
});

configurePdfJsWorker(pdfjsLib as any);

export default function PDFViewerSecure({ shikakuId, shikakuName, proxyBaseUrl, onPageInfoChange }: Props) {
  const [pdfDoc, setPdfDoc] = useState<any | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [panRender, setPanRender] = useState<PanState>({ x: 0, y: 0 });
  const [isInteracting, setIsInteracting] = useState(false);

  const panRef = useRef<PanState>({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const swipeStartXRef = useRef<number | null>(null);
  const pinchRef = useRef<{ distance: number; zoom: number } | null>(null);

  const watermarkText = useMemo(
    () => (auth.currentUser?.displayName || auth.currentUser?.uid || shikakuName || 'User') as string,
    [shikakuName],
  );

  const commitPan = useCallback((nextPan: PanState) => {
    panRef.current = nextPan;
    if (rafRef.current !== null) return;

    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      setPanRender(panRef.current);
    });
  }, []);

  const resetPan = useCallback(() => {
    panRef.current = { x: 0, y: 0 };
    setPanRender({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPdf = async () => {
      setError(null);
      setLoading(true);
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('ログインが必要です');

        const idToken = await user.getIdToken();
        const base = resolvePdfProxyBaseUrl(proxyBaseUrl);
        if (!base) throw new Error('PDF proxy URL not configured (VITE_PDF_PROXY_URL or VITE_FIREBASE_PROJECT_ID is required)');

        const url = `${base}/pdf?shikakuId=${encodeURIComponent(shikakuId)}`;
        const loadingTask = pdfjsLib.getDocument({
          url,
          httpHeaders: { Authorization: `Bearer ${idToken}` },
          withCredentials: false,
          disableWorker: false,
          useWorkerFetch: true,
          rangeChunkSize: 262144,
        });

        const pdf = await loadingTask.promise;
        if (cancelled) return;

        setPdfDoc(pdf);
        setNumPages(pdf.numPages || 0);
        setCurrentPage(1);
        setZoom(1);
        resetPan();
      } catch (err: any) {
        console.error('PDF render error', err);
        setError(err.message || 'PDFの読み込みに失敗しました');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [shikakuId, proxyBaseUrl, resetPan]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!numPages) return;
    onPageInfoChange?.(currentPage, numPages);
  }, [currentPage, numPages, onPageInfoChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'c', 'S', 'P', 'C'].includes((e as any).key)) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const clampZoom = (value: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Number(value.toFixed(2))));

  const setZoomAndPan = (nextZoom: number) => {
    const clamped = clampZoom(nextZoom);
    setZoom(clamped);
    if (clamped === 1) resetPan();
  };

  const goToPage = (page: number) => {
    if (!numPages) return;
    const clamped = Math.max(1, Math.min(numPages, page));
    setCurrentPage(clamped);
    setZoom(1);
    resetPan();
  };

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsInteracting(true);

    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      pinchRef.current = {
        distance: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
        zoom,
      };
      swipeStartXRef.current = null;
      panStartRef.current = null;
      return;
    }

    const touch = e.touches[0];
    if (!touch) return;
    if (zoom > 1) {
      panStartRef.current = { x: touch.clientX, y: touch.clientY, panX: panRef.current.x, panY: panRef.current.y };
      return;
    }

    swipeStartXRef.current = touch.clientX;
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const [a, b] = e.touches;
      const distance = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
      const ratio = distance / pinchRef.current.distance;
      setZoomAndPan(pinchRef.current.zoom * ratio);
      return;
    }

    if (zoom > 1 && panStartRef.current && e.touches.length === 1) {
      const touch = e.touches[0];
      const dx = touch.clientX - panStartRef.current.x;
      const dy = touch.clientY - panStartRef.current.y;
      commitPan({ x: panStartRef.current.panX + dx, y: panStartRef.current.panY + dy });
    }
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length < 2) pinchRef.current = null;

    if (zoom > 1) {
      panStartRef.current = null;
      swipeStartXRef.current = null;
      if (e.touches.length === 0) setIsInteracting(false);
      return;
    }

    const startX = swipeStartXRef.current;
    const endX = e.changedTouches[0]?.clientX;
    swipeStartXRef.current = null;
    if (startX === null || endX === undefined) {
      if (e.touches.length === 0) setIsInteracting(false);
      return;
    }

    const deltaX = endX - startX;
    if (deltaX <= -50) goToPage(currentPage + 1);
    if (deltaX >= 50) goToPage(currentPage - 1);

    if (e.touches.length === 0) setIsInteracting(false);
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoom <= 1) return;
    setIsInteracting(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, panX: panRef.current.x, panY: panRef.current.y };
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoom <= 1 || !panStartRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    commitPan({ x: panStartRef.current.panX + dx, y: panStartRef.current.panY + dy });
  };

  const onMouseUp = () => {
    panStartRef.current = null;
    setIsInteracting(false);
  };

  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    setIsInteracting(true);
    setZoomAndPan(zoom + (e.deltaY < 0 ? 0.08 : -0.08));
    window.setTimeout(() => setIsInteracting(false), 120);
  };

  return (
    <div
      onContextMenu={(e) => e.preventDefault()}
      style={{ userSelect: 'none', touchAction: 'none' }}
      className="h-full"
    >
      {loading && <div>PDF 読み込み中...</div>}
      {error && <div className="text-red-600">エラー: {error}</div>}

      {pdfDoc && numPages > 0 ? (
        <div className="h-full flex flex-col gap-2">
          <div className="flex items-center justify-between bg-white rounded-xl shadow-sm px-3 py-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setZoomAndPan(zoom + 0.1)}
                className="w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center"
                aria-label="PDFを拡大"
              >
                +
              </button>
              <button
                type="button"
                onClick={() => setZoomAndPan(zoom - 0.1)}
                className="w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center"
                aria-label="PDFを縮小"
              >
                −
              </button>
            </div>
            <div className="text-xs text-gray-600">{Math.round(zoom * 100)}%</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="px-2 py-1 text-xs rounded bg-gray-100 disabled:opacity-40"
              >
                前へ
              </button>
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= numPages}
                className="px-2 py-1 text-xs rounded bg-gray-100 disabled:opacity-40"
              >
                次へ
              </button>
            </div>
          </div>

          <div
            className="bg-white rounded-xl shadow-sm flex-1 min-h-0 overflow-hidden touch-none"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onWheel={onWheel}
          >
            <div
              style={{
                transform: `translate(${panRender.x}px, ${panRender.y}px) scale(${zoom})`,
                transformOrigin: 'top center',
                transition: isInteracting ? 'none' : 'transform 120ms ease-out',
                height: '100%',
                willChange: 'transform',
              }}
            >
              <PageCanvas pdf={pdfDoc} pageNumber={currentPage} watermark={watermarkText} />
            </div>
          </div>

          <div className="z-10 bg-white rounded-xl shadow-sm px-4 py-2 flex-shrink-0">
            <input
              type="range"
              min={1}
              max={numPages}
              step={1}
              value={currentPage}
              onChange={(e) => goToPage(Number(e.target.value))}
              className="w-full viewtext-slider"
              aria-label="ページ送りスライダー"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
